<template>
  <div class="w-[800px] mx-auto h-[800px] m-4" ref="appcparent">
    <ModalBox
      v-model="showModal"
      :backgroundDismissable="false"
      :showCloseButton="false"
    >
      <div>
        <h1 class="text-4xl font-bold text-center loading">
          {{ editedRel ? "Modifier" : "Ajouter" }} la relation
        </h1>

        <div class="mt-4">
          <template v-for="(rel, index) of relations" :key="index">
            <div
              class="flex items-center p-2 py-4 text-lg font-bold cursor-pointer gap-x-2 hover:bg-base-300"
              :class="{
                '!bg-primary/10': editedRel?.relation == rel.id
              }"
              :style="{
                color: rel.color
              }"
              @click="setRelation(rel)"
            >
              <i
                :style="{
                  color: rel.color
                }"
                class="flex items-center justify-center w-4 h-4 mr-2 text-xl text-center fas"
                :class="rel.icon"
              ></i
              >{{ rel.text }}
            </div>
          </template>
        </div>

        <div class="flex justify-end mt-4 gap-x-4">
          <button
            @click="closeModal"
            class="btn"
            :class="{ loading: settingRelation }"
          >
            Fermer
          </button>

          <button
            @click="deleteRelation"
            v-if="editedRel"
            class="btn btn-error"
            :class="{ loading: deletingRelation }"
          >
            Supprimer
          </button>
        </div>
      </div>
    </ModalBox>
    <LegendAPP
      ref="legend"
      @download="$emit('makereport')"
      :relations="relations"
    />

    <div class="fixed left-0 flex flex-col gap-y-2 top-32 h-4/6">
      <liste-acteurs
        @itemhover="focusHovered"
        @itemrelease="hovered = null"
        :items="circles"
      />

      <FiltreAPP
        v-if="circle"
        @filterupdated="applyFilters"
        :items="circles"
        :relations="relations"
      />
    </div>
    <div class="w-full h-full" ref="diagram">
      <div class="absolute inset-0 z-40 bg-opacity-40">
        <template v-for="(rel, index) of relationships" :key="index">
          <div class="group">
            <div
              class="fixed top-[50%] translate-y-[-50%]   left-[50%] translate-x-[-50%] z-30 hidden p-6 animate-animated animate-fadeIn text-lg  group-hover:block bg-neutral text-neutral-content rounded-box"
            >
              Relation {{ relations.find(x => x.id == rel.relation)?.text }}

              <div class="mt-2 text-sm italics">
                Cliquez pour modifier ou supprimer
              </div>
            </div>
            <div
              v-if="circlePositionCache.length"
              class="group"
              @click="!analysis && openRelationEdit(rel)"
            >
              <div
                v-if="
                  filteredCircles.map(x => x.id).includes(rel.items[0]) &&
                    filteredCircles.map(x => x.id).includes(rel.items[1]) &&
                    (!filters || filters?.relations?.includes(rel.relation))
                "
                class="absolute h-2 bg-black cursor-pointer animate-animated animate-fadeIn hover:z-10 hover:h-2"
                :style="{
                  width: getRelationPosition(rel.items)?.distance + 'px',
                  top: getRelationPosition(rel.items)?.yMid + 'px',
                  left: getRelationPosition(rel.items)?.xMid + 'px',
                  transform: `rotate(${
                    getRelationPosition(rel.items)?.slope
                  }deg`,
                  backgroundColor: true
                    ? 'transparent'
                    : relations.find(x => x.id == rel.relation)?.color
                }"
              >
                <svg
                  v-if="[0, 0].includes(rel.relation)"
                  height="13"
                  class="absolute bottom-[0px]"
                  :width="getRelationPosition(rel.items)?.distance"
                  style="background: transparent"
                >
                  <line
                    x1="0"
                    y1="4"
                    :x2="getRelationPosition(rel.items)?.distance"
                    y2="4"
                    style="stroke-width: 4"
                    :style="{
                      stroke: relations.find(x => x.id == rel.relation)?.color
                    }"
                  />
                  <line
                    x1="0"
                    y1="8"
                    :x2="getRelationPosition(rel.items)?.distance"
                    y2="8"
                    style="stroke: rgba(255, 0, 255, 0); stroke-width: 4"
                  />
                  <line
                    x1="0"
                    y1="12"
                    :x2="getRelationPosition(rel.items)?.distance"
                    y2="12"
                    style="stroke: rgba(255, 0, 0, 0.6); stroke-width: 6"
                    :style="{
                      stroke: relations.find(x => x.id == rel.relation)?.color
                    }"
                  />
                </svg>

                <svg
                  v-if="[2, 4].includes(rel.relation)"
                  height="13"
                  class="absolute bottom-[0px]"
                  :width="getRelationPosition(rel.items)?.distance"
                  style="background: transparent"
                >
                  <line
                    x1="0"
                    y1="4"
                    :x2="getRelationPosition(rel.items)?.distance"
                    y2="4"
                    style="stroke: rgba(255, 0, 255, 0); stroke-width: 4"
                  />
                  <line
                    v-for="multiple of Math.ceil(
                      getRelationPosition(rel.items)?.distance / 20
                    )"
                    :key="multiple"
                    :x1="20 * multiple + 6"
                    y1="8"
                    :x2="(multiple + 1) * 20"
                    y2="8"
                    style="stroke-width: 4"
                    :style="{
                      stroke: relations.find(x => x.id == rel.relation)?.color
                    }"
                  />
                  <line
                    x1="0"
                    y1="12"
                    :x2="getRelationPosition(rel.items)?.distance"
                    y2="12"
                    style="stroke: rgba(255, 0, 255, 0); stroke-width: 6"
                  />
                </svg>

                <svg
                  v-if="[3].includes(rel.relation)"
                  height="13"
                  class="absolute bottom-[0px]"
                  :width="getRelationPosition(rel.items)?.distance"
                  style="background: transparent"
                >
                  <line
                    x1="0"
                    y1="4"
                    :x2="getRelationPosition(rel.items)?.distance"
                    y2="4"
                    style="stroke: rgba(255, 0, 255, 0); stroke-width: 4"
                  />
                  <line
                    v-for="multiple of Math.ceil(
                      getRelationPosition(rel.items)?.distance / 8
                    )"
                    :key="multiple"
                    :x1="8 * multiple + 3"
                    y1="8"
                    :x2="(multiple + 1) * 8"
                    y2="8"
                    style="stroke-width: 4"
                    :style="{
                      stroke: relations.find(x => x.id == rel.relation)?.color
                    }"
                  />
                  <line
                    x1="0"
                    y1="12"
                    :x2="getRelationPosition(rel.items)?.distance"
                    y2="12"
                    style="stroke: rgba(255, 0, 255, 0); stroke-width: 6"
                  />
                </svg>

                <svg
                  v-if="[1, 5].includes(rel.relation)"
                  height="13"
                  class="absolute bottom-[0px]"
                  :width="getRelationPosition(rel.items)?.distance"
                  style="background: transparent"
                >
                  <line
                    x1="0"
                    y1="4"
                    :x2="getRelationPosition(rel.items)?.distance"
                    y2="4"
                    style="stroke: rgba(255, 0, 255, 0); stroke-width: 4"
                  />
                  <line
                    :x1="0"
                    y1="8"
                    :x2="getRelationPosition(rel.items)?.distance"
                    y2="8"
                    style="stroke-width: 4"
                    :style="{
                      stroke: relations.find(x => x.id == rel.relation)?.color
                    }"
                  />
                  <line
                    x1="0"
                    y1="12"
                    :x2="getRelationPosition(rel.items)?.distance"
                    y2="12"
                    style="stroke: rgba(255, 0, 255, 0); stroke-width: 6"
                  />
                </svg>

                <div class="relative flex items-center justify-center -top-6">
                  <i
                    :style="{
                      color: relations.find(x => x.id == rel.relation)?.color,
                      border:
                        '1px solid ' +
                        relations.find(x => x.id == rel.relation)?.color
                    }"
                    class="flex items-center justify-center text-3xl text-center bg-white rounded-full fas btn-square"
                    :class="relations.find(x => x.id == rel.relation)?.icon"
                  ></i>
                </div>
              </div>
            </div>
          </div>
        </template>

        <template v-for="(item, type) in filteredCircles" :key="type">
          <APPBullet
            v-if="getPosition(item)"
            :item="item"
            @click="!analysis && addToSelected(item)"
            class="z-20"
            :selected="selected.includes(item)"
            :shouldhover="hovered && item.id == hovered.id"
            :position="getPosition(item)"
            :spacing="spacing"
          />
        </template>
      </div>
      <canvas ref="appc" class="z-0 animate-animated animate-zoomIn"></canvas>
    </div>
  </div>
</template>

<script>
import LegendAPP from "@/components/APPC/LegendAPP.vue";
import APPBullet from "@/components/APPC/APPBullet.vue";
import ListeActeurs from "@/components/APPC/ListeActeurs.vue";
import FiltreAPP from "@/components/APPC/FiltreAPP.vue";
import ModalBox from "@/components/ModalBox.vue";
import globalMixins from "@/utils/mixins/mixin.js";
import * as htmlToImage from "html-to-image";
export default {
  name: "RelationDiagram",
  components: { LegendAPP, APPBullet, ListeActeurs, FiltreAPP, ModalBox },
  mixins: [globalMixins],
  props: {
    circles: Array,
    analysis: { type: Boolean, default: false }
  },
  data() {
    return {
      showModal: false,
      circle: 0,
      editedRel: null,
      vueCanvas: null,
      settingRelation: false,
      deletingRelation: false,
      relationships: [],
      relationData: null,
      relations: [
        { text: "Amicale", icon: "fa-heart", color: "#056a2e", id: 1 },
        { text: "Cordiale", icon: "fa-sun", color: "#53972a", id: 2 },
        //  { text: "Franche", icon: "fa-smile-beam", color: "#94b713", id: 3 },
        { text: "Ambigüe", icon: "fa-cloud-sun", color: "#f9b129", id: 3 },
        //{ text: "Froide", icon: "fa-cloud", color: "#ef4444", id: 5 },
        { text: "Tendue", icon: "fa-cloud", color: "#c5301c", id: 4 },
        {
          text: "Conflictuelle",
          icon: "fa-poo-storm",
          color: "#a2231c",
          id: 5
        }
      ],
      circlePositionCache: [],
      selected: [],
      hovered: null,
      width: 0,
      height: 0,
      distance: 60,
      vals: [5, 4, 3],
      colors: [
        "rgba(0,91,255,0.6)",
        "rgba(0,91,255,0.4)",
        "rgba(0,91,255,0.2)",
        "rgba(185,230,226,0.5)",

        "rgba(241,250,249,0.5)"
      ],
      lineColor: "rgb(255,60,60)",
      ballColor: "rgb(60,255,60)",
      filters: null
    };
  },

  computed: {
    filteredCircles() {
      if (!this.filters) return this.circles;

      return this.circles.filter(
        x =>
          this.filters.type.includes(x.type.id) &&
          this.filters.implication.includes(x.implication.id) &&
          this.filters.influence.includes(x.influence.id)
      );
    },
    typesGroup() {
      return window._.groupBy(this.circles, function(circle) {
        return circle.type.id;
      });
    },
    radius() {
      return this.height / 2;
    },

    spacing() {
      return this.radius / 6;
    }
  },

  created() {
    // Setup a timer
    var timeout;
    var self = this;
    window.addEventListener(
      "resize",
      function() {
        console.log("no debounce");

        // If there's a timer, cancel it
        if (timeout) {
          this.clearTimeout(timeout);
        }

        // Setup the new requestAnimationFrame()
        timeout = setTimeout(self.drawAll, 400);
      },
      false
    );
  },
  unmounted() {
    window.removeEventListener("resize", this.drawAll);
  },

  async mounted() {
    await this.fetchData();
    this.drawAll();
  },

  methods: {
    openRelationEdit(rel) {
      this.editedRel = rel;
      this.showModal = true;
    },

    closeModal() {
      this.selected = [];
      this.editedRel = null;
      this.showModal = false;
    },

    async deleteRelation() {
      this.deletingRelation = true;

      await this.$axios.delete(
        `/RelationPartiePrenantesCommands/DeleteRelationPartiePrenante/${this.editedRel.relationPartiePrenanteID}`
      );
      this.relationships = this.relationships.filter(x => x != this.editedRel);
      this.editedRel = null;
      this.selected = [];
      this.deletingRelation = false;
      this.showModal = false;
    },

    getRelationPosition(items) {
      console.log("Items are");
      console.log(items);
      var first = this.filteredCircles.find(x => x.id == items[0]);
      var second = this.filteredCircles.find(x => x.id == items[1]);
      console.log("First and second are :");
      console.log(this.getPosition(first));
      console.log(this.getPosition(second));
      try {
        var x2 = this.getPosition(first).x;
      } catch (error) {
        console.log("Hey");
        console.log(error);
        return;
      }

      var y2 = this.getPosition(first).y;
      var x1 = this.getPosition(second).x;
      var y1 = this.getPosition(second).y;

      console.log("x1: ", x1);
      console.log("y1: ", y1);
      console.log("x2: ", x2);
      console.log("y2: ", y2);
      var distance = Math.sqrt((x1 - x2) * (x1 - x2) + (y1 - y2) * (y1 - y2));
      var xMid = (x1 + x2) / 2;
      xMid = xMid - distance / 2;
      var yMid = (y1 + y2) / 2;

      var slope = (Math.atan2(y1 - y2, x1 - x2) * 180) / Math.PI;

      return { distance, xMid, yMid, slope };
    },
    mode(arr) {
      return window._.chain(arr)
        .countBy()
        .pairs()
        .value()
        .sort((a, b) => b[1] - a[1])
        .filter((e, i, a) => e[1] === a[0][1]);
    },
    async fetchData() {
      var response;
      if (!this.analysis) {
        await this.$axios.get(
          "/RelationPartiePrenantesQueries/GetRelationPartiePrenanteAnalysis/" +
            this.currentFile.fileID
        );
        response = await this.$axios.get(
          "/ApRelationStores/GetApRelationStoresByFileID/" +
            this.currentFile.fileID
        );
        this.relationships = response.data.map(x => ({
          ...x,
          items: x.items.split(","),
          relation: Math.round(
            this.mode(x.relation.split(",").map(x => +x))
              .map(x => +x[0])
              .reduce((a, b) => a + (b ?? 0)) / this.mode(x.relation).length
          )
        }));
      } else {
        response = await this.$axios.get(
          "/RelationPartiePrenantesQueries/GetRelationPartiePrenanteAnalysis/" +
            this.currentFile.fileID
        );

        this.relationships = response.data.map(x => ({
          ...x,
          relation: Math.round(
            this.mode(x.relation)
              .map(x => +x[0])
              .reduce((a, b) => a + (b ?? 0)) / this.mode(x.relation).length
          )
        }));
      }
    },
    async setRelation(rel) {
      this.settingRelation = true;

      if (this.editedRel) {
        const editedRel = { ...this.editedRel, relation: rel.id };
        this.relationships.find(x => x == this.editedRel).relation = rel.id;
        await this.updateRelation(editedRel);
        this.editedRel = null;
      } else {
        const newRelation = {
          items: [...this.selected.map(x => x.id)],
          partiePrenanteID_1: this.selected[0].id,
          partiePrenanteID_2: this.selected[1].id,
          relation: rel.id,
          personID: this.myParticipant.participantID,
          fileID: this.currentFile.fileID
        };

        const dataResponse = await this.createRelation(newRelation);
        this.relationships.push({ ...newRelation, ...dataResponse });
      }
      this.selected = [];

      this.settingRelation = false;
      this.showModal = false;
    },

    async createRelation(relation) {
      var response = await this.$axios.post(
        "/RelationPartiePrenantesCommands/PostRelationPartiePrenante",
        relation
      );

      return response.data;
    },

    async updateRelation(relation) {
      var response = await this.$axios.put(
        `/ApRelationStores/PutApRelationStore/${relation.apRelationID}`,
        {
          ...relation,
          items: relation.items.join(","),
          relation: relation.relation.toString()
        }
      );

      return response.data;
    },

    addToSelected(item) {
      if (this.selected.includes(item)) {
        this.selected = this.selected.filter(x => x != item);
      } else {
        this.selected = [...this.selected, item];
        if (this.selected.length > 1) {
          this.editedRel = this.relationships.find(
            x =>
              x.items.includes(this.selected[0].id) &&
              x.items.includes(this.selected[1].id)
          );
          this.showModal = true;
          //this.$swal.fire({ icon: "info", title: "Choissisez la relation" });
          return;
        }
      }
    },
    applyFilters(filters) {
      this.filters = filters;
    },
    focusHovered(e) {
      this.hovered = e;
    },
    async downloadImage() {
      var dataUrl = await htmlToImage.toPng(this.$refs.diagram);

      var dataUrl2 = await htmlToImage.toPng(
        this.$refs.legend.getLengendElement()
      );

      var link = document.createElement("a");
      link.download = "relation-diagram.jpeg";
      link.href = dataUrl;
      var el = this.$refs.legend.getLengendElement();
      var rect = el.getBoundingClientRect();
      return {
        data: dataUrl,
        legend: dataUrl2,
        width: (rect.width || el.offsetWidth) * 0.2645833333,
        height: (rect.height || el.offsetHeight) * 0.2645833333,
        title: "Analyse Relationnel des acteurs"
      };
    },

    drawAll() {
      if (!this.circles || !this.circles.length) {
        return;
      }
      this.circlePositionCache = [];

      console.log("Circles drawings");
      var c = this.$refs.appc;
      var par = this.$refs.appcparent;
      this.width = Math.min(window.innerHeight, window.innerWidth) - 20;
      this.height = this.width;
      par.style.width = this.width + "px";
      par.style.height = this.width + "px";
      var ctx = c.getContext("2d");
      ctx.canvas.width = this.width;
      ctx.canvas.height = this.height;
      this.vueCanvas = ctx;

      // this.simulateVals();

      //this.simulateVals();
      this.circle = 1;
      this.drawCircle();
      /*   for (let index = 0; index < 2; index++) {
        this.circle = index;
        this.drawCircle();
      } */
      this.simulateVals();

      //this.circle = 5;
      //this.drawCircle();
    },
    getPosition(item) {
      if (!item) return;
      return this.circlePositionCache.filter(x => x.item.id == item.id)[0];
    },
    groupByInfluence(items) {
      return window._.groupBy(items, function(circle) {
        return circle.influence.id;
      });
    },
    simulateVals() {
      //const total = this.circles.length;
      //console.log("Total is :" + total);
      //console.log(this.typesGroup);
      //this.drawFillCircle(this.colors[parseInt(1) - 1], 0, 2 * Math.PI);
      this.drawCirclesForAngle(2 * Math.PI, 0, this.circles);
    },
    drawCircle(color = this.lineColor, stroke = true) {
      this.vueCanvas.beginPath();
      if (this.circle < 5 && this.circle != 0) {
        this.vueCanvas.shadowColor = "black";
        this.vueCanvas.shadowBlur = 10;
        this.vueCanvas.shadowOffsetX = 0;
        this.vueCanvas.shadowOffsetY = 0;
      }
      this.vueCanvas.beginPath();
      this.vueCanvas.strokeStyle = color;
      this.vueCanvas.fillStyle = color;
      this.vueCanvas.arc(
        this.radius,
        this.radius,
        this.getCircleRadius(this.circle++),
        0,
        2 * Math.PI
      );
      if (stroke) {
        this.vueCanvas.fillStyle = "#fff";
        this.vueCanvas.strokeStyle = "#fff";
        this.vueCanvas.fill();
        this.vueCanvas.stroke();
      } else {
        this.vueCanvas.fill();
      }
      this.vueCanvas.shadowColor = "transparent";
    },

    drawFillCircle(color, start, end) {
      /*   console.log(
        "Je dessine les segment avec " +
          color +
          " Je commence à : " +
          start +
          " et je m'arrete à: " +
          end
      ); */
      this.vueCanvas.beginPath();
      this.vueCanvas.fillStyle = color;
      this.vueCanvas.moveTo(this.getCircleRadius(1), this.getCircleRadius(1));
      this.vueCanvas.arc(
        this.radius,
        this.radius,
        this.getCircleRadius(1),
        start,
        end
      );
      this.vueCanvas.lineTo(this.getCircleRadius(1), this.getCircleRadius(1));
      this.vueCanvas.fill();
    },

    //Dessiner les circles pour un type (une sections)
    drawCirclesForAngle(angle, offset, circles) {
      var circlePositionCache = [...this.circlePositionCache];
      //Recuperer  par groupe d'influence (groupe de ligne)
      const lines = { 4: circles };
      for (const [lineNumber, item] of Object.entries(lines)) {
        console.log("Nous somme dans l'influence", lineNumber);
        console.log(item.length);
        console.log(angle);
        var angleSpacing = angle / item.length;
        console.log("Angle Spacing of " + lineNumber + " = " + angleSpacing);
        for (let index = 0; index < item.length; index++) {
          var x = Math.cos(offset + angleSpacing * (index + 1));
          var y = Math.sin(offset + angleSpacing * (index + 1));

          //this.drawCircleItem(this.ballColor, 5 - lineNumber, x, y);
          circlePositionCache.push({
            item: item[index],
            x: this.radius + x * this.getCircleRadius(5 - lineNumber),
            y: this.radius + y * this.getCircleRadius(5 - lineNumber)
          });
        }
      }

      this.circlePositionCache = circlePositionCache;
    },

    drawCircleItem(color, level, x, y) {
      //console.log("Je dessin " + color + " " + level + " " + x + " " + y);
      this.vueCanvas.beginPath();
      this.vueCanvas.fillStyle = color;
      this.vueCanvas.moveTo(0, 0);
      this.vueCanvas.arc(
        this.radius + x * this.getCircleRadius(level),
        this.radius + y * this.getCircleRadius(level),
        20,
        0,
        2 * Math.PI
      );
      this.vueCanvas.fill();

      this.vueCanvas.beginPath();
      this.vueCanvas.fillStyle = "#000";
      this.vueCanvas.arc(
        this.radius + x * this.getCircleRadius(level) + 12,
        this.radius + y * this.getCircleRadius(level) - 12,
        10,
        0,
        2 * Math.PI
      );
      this.vueCanvas.fill();
    },

    getCircleRadius(circle) {
      return this.radius - this.spacing * circle;
    },

    clearRect() {
      // clear canvas
      this.vueCanvas.clearRect(0, 0, 400, 200);

      // draw rect
      this.vueCanvas.beginPath();
      this.vueCanvas.rect(20, 20, this.rectWidth, 100);
      this.vueCanvas.stroke();
    },
    drawRect() {
      this.vueCanvas.moveTo(0, 0);
      this.vueCanvas.lineTo(200, 100);
      this.vueCanvas.stroke();
    },
    addWidth() {
      this.rectWidth += 10;
      this.drawRect();
    },
    subWidth() {
      this.rectWidth -= 10;
      this.drawRect();
    }
  }
};
</script>

<style scoped></style>>
